import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash, UploadSimple, Image as ImageIcon, FileXls, X, CheckCircle, FilmSlate, DownloadSimple } from "@phosphor-icons/react";
import { VEHICLE_TYPES, BRAND_TYPES, PARTS_CONDITIONS, PACKAGING_CONDITIONS } from "@/lib/config";

const CATEGORIES = ["Mixed", "Service Parts", "Body Parts", "Mechanical Parts", "Electrical", "Engine Parts", "Accessories"];
const STATES = ["Maharashtra", "Karnataka", "Tamil Nadu", "Gujarat", "Rajasthan", "Haryana", "Delhi", "Uttar Pradesh", "Telangana", "West Bengal", "Punjab", "Kerala"];

const DECLARATIONS = [
  "I confirm the inventory data provided is accurate and I have the legal right to sell these goods.",
  "I understand SparesHub will sanitize my file and that the minimum lot MRP value is ₹5,00,000.",
  "I agree that all negotiations and settlement will be managed by SparesHub under escrow.",
];

const empty = {
  title: "", description: "", category: "", brand: "", vehicle_type: "", brand_type: "", city: "", state: "", condition: "", packaging_condition: "",
  total_units: "", unique_skus: "", mrp_value: "", asking_price: "",
  sample_inventory: [{ sku: "", description: "", qty: "", mrp: "" }],
  image_ids: [], video_ids: [], original_excel_file_id: null,
};

function Section({ title, step, children }) {
  return (
    <div className="rounded-sm border border-border bg-white">
      <div className="flex items-center gap-3 border-b border-border bg-navy-50 px-6 py-4">
        <span className="stat flex h-7 w-7 items-center justify-center rounded-sm bg-navy text-sm font-bold text-white">{step}</span>
        <h2 className="font-display font-bold text-navy">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

export default function SellerLotForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(empty);
  const [images, setImages] = useState([]); // {id, filename, url}
  const [videos, setVideos] = useState([]); // {id, filename, url}
  const [excel, setExcel] = useState(null); // {id, filename}
  const [decls, setDecls] = useState([false, false, false]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!editing);

  useEffect(() => {
    if (!editing || !user) return;
    const applyLot = (lot) => {
      if (lot) {
        setForm({
          title: lot.title, description: lot.description, category: lot.category, brand: lot.brand,
          vehicle_type: lot.vehicle_type || "", brand_type: lot.brand_type || "",
          city: lot.city, state: lot.state, condition: lot.condition || "", packaging_condition: lot.packaging_condition || "", total_units: lot.total_units,
          unique_skus: lot.unique_skus, mrp_value: lot.mrp_value, asking_price: lot.asking_price,
          sample_inventory: lot.sample_inventory?.length ? lot.sample_inventory : empty.sample_inventory,
          image_ids: lot.image_ids || [], video_ids: lot.video_ids || [], original_excel_file_id: lot.original_excel_file_id,
        });
        setImages((lot.image_ids || []).map((im) => ({ id: im, url: im.startsWith("http") ? im : `/api/files/${im}`, filename: "image" })));
        setVideos((lot.video_ids || []).map((vi) => ({ id: vi, url: vi.startsWith("http") ? vi : `/api/files/${vi}`, filename: "video" })));
      }
      setLoaded(true);
    };
    const req = isAdmin
      ? api.get(`/admin/lots/${id}`).then(({ data }) => applyLot(data))
      : api.get("/seller/lots").then(({ data }) => applyLot(data.lots.find((l) => l.id === id)));
    req.catch(() => setLoaded(true));
  }, [id, editing, isAdmin, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImages = async (files) => {
    const room = 8 - form.image_ids.length;
    if (room <= 0) { toast.error("You can upload a maximum of 8 images"); return; }
    const toUpload = files.slice(0, room);
    if (files.length > room) toast.error(`Only ${room} more image(s) allowed — maximum is 8`);
    setUploading(true);
    try {
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append("file", file); fd.append("purpose", "media");
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((im) => [...im, { id: data.id, filename: data.filename, url: URL.createObjectURL(file) }]);
        setForm((f) => ({ ...f, image_ids: [...f.image_ids, data.id] }));
      }
      toast.success("Images uploaded");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const uploadExcel = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("purpose", "original_excel");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExcel({ id: data.id, filename: data.filename });
      set("original_excel_file_id", data.id);
      toast.success("Inventory file uploaded");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const validateVideoDuration = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration); };
      v.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      v.src = url;
    } catch { resolve(null); }
  });

  const uploadVideos = async (files) => {
    setUploading(true);
    let uploaded = 0;
    try {
      for (const file of files) {
        const dur = await validateVideoDuration(file);
        if (dur === null) { toast.error(`Couldn't read "${file.name}". Please upload a standard MP4/MOV video under 600 seconds.`); continue; }
        if (dur > 600.5) { toast.error(`"${file.name}" is ${Math.round(dur)}s — each video must be 600 seconds (10 minutes) or shorter`); continue; }
        const fd = new FormData();
        fd.append("file", file); fd.append("purpose", "media");
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setVideos((v) => [...v, { id: data.id, filename: data.filename, url: URL.createObjectURL(file) }]);
        setForm((f) => ({ ...f, video_ids: [...f.video_ids, data.id] }));
        uploaded += 1;
      }
      if (uploaded > 0) toast.success(uploaded > 1 ? `${uploaded} videos uploaded` : "Video uploaded");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const removeVideo = (vid) => {
    setVideos((v) => v.filter((x) => x.id !== vid));
    setForm((f) => ({ ...f, video_ids: f.video_ids.filter((x) => x !== vid) }));
  };

  const removeImage = (imgId) => {
    setImages((im) => im.filter((x) => x.id !== imgId));
    setForm((f) => ({ ...f, image_ids: f.image_ids.filter((x) => x !== imgId) }));
  };

  const setRow = (i, k, v) => setForm((f) => { const rows = [...f.sample_inventory]; rows[i] = { ...rows[i], [k]: v }; return { ...f, sample_inventory: rows }; });
  const addRow = () => setForm((f) => ({ ...f, sample_inventory: [...f.sample_inventory, { sku: "", description: "", qty: "", mrp: "" }] }));
  const delRow = (i) => setForm((f) => ({ ...f, sample_inventory: f.sample_inventory.filter((_, idx) => idx !== i) }));

  const payload = (submit) => ({
    title: form.title, description: form.description, category: form.category, brand: form.brand,
    vehicle_type: form.vehicle_type, brand_type: form.brand_type,
    city: form.city, state: form.state, condition: form.condition, packaging_condition: form.packaging_condition,
    total_units: Number(form.total_units || 0), unique_skus: Number(form.unique_skus || 0),
    mrp_value: Number(form.mrp_value || 0), asking_price: Number(form.asking_price || 0),
    sample_inventory: form.sample_inventory.filter((r) => r.sku || r.description).map((r) => ({ sku: r.sku, description: r.description, qty: Number(r.qty || 0), mrp: Number(r.mrp || 0) })),
    image_ids: form.image_ids, video_ids: form.video_ids, original_excel_file_id: form.original_excel_file_id,
    declarations_accepted: submit ? decls.every(Boolean) : false, submit,
  });

  const save = async (submit) => {
    if (!form.title) { toast.error("Lot title is required"); return; }
    if (submit) {
      if (!form.vehicle_type) { toast.error("Please select a vehicle type"); return; }
      if (!form.brand_type) { toast.error("Please select a brand type"); return; }
      if (Number(form.mrp_value) < 500000) { toast.error("Lot MRP value must be at least ₹5,00,000 to submit"); return; }
      if (Number(form.asking_price) <= 0) { toast.error("Asking price is required"); return; }
      if (form.image_ids.length < 4) { toast.error("Please upload at least 4 images"); return; }
      if (form.image_ids.length > 8) { toast.error("Please upload no more than 8 images"); return; }
      if (form.video_ids.length < 1) { toast.error("Please upload at least 1 video (max 600 seconds)"); return; }
      if (!isAdmin && !decls.every(Boolean)) { toast.error("Please accept all declarations to submit"); return; }
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/seller/lots/${id}`, payload(submit));
      else await api.post("/seller/lots", payload(submit));
      toast.success(submit ? (isAdmin ? "Lot published" : "Lot submitted for verification") : "Draft saved");
      nav(isAdmin ? "/admin" : "/seller");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!loaded) return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-navy/20 border-t-brand" /></div>;

  return (
    <div className="mx-auto max-w-4xl px-5 py-10 lg:px-8">
      <Link to={isAdmin ? "/admin" : "/seller"} className="text-sm text-muted-foreground hover:text-brand">← Back to dashboard</Link>
      <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-navy">{editing ? "Edit lot" : isAdmin ? "Create & publish a lot" : "Submit a new lot"}</h1>
      <p className="mt-1 text-muted-foreground">{isAdmin ? "Admin-created lots are published immediately. Every lot needs 4 to 8 photos and one walkthrough video." : "Provide accurate details. Our team verifies every lot before it goes live."}</p>

      <div className="mt-8 space-y-6">
        <Section title="Lot details" step="1">
          <div className="grid gap-4">
            <div><Label>Lot title *</Label><Input data-testid="lot-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 rounded-sm" placeholder="e.g. Maruti OEM Service Parts — Closing Dealership" /></div>
            <div><Label>Description</Label><Textarea data-testid="lot-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="mt-1.5 rounded-sm" placeholder="What's in the lot, condition, packaging, years..." /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger className="mt-1.5 rounded-sm" data-testid="lot-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Vehicle type *</Label>
                <Select value={form.vehicle_type} onValueChange={(v) => set("vehicle_type", v)}><SelectTrigger className="mt-1.5 rounded-sm" data-testid="lot-vehicle-type"><SelectValue placeholder="Select vehicle type" /></SelectTrigger>
                  <SelectContent>{VEHICLE_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Brand type *</Label>
                <Select value={form.brand_type} onValueChange={(v) => set("brand_type", v)}><SelectTrigger className="mt-1.5 rounded-sm" data-testid="lot-brand-type"><SelectValue placeholder="OEM / Aftermarket / Unbranded" /></SelectTrigger>
                  <SelectContent>{BRAND_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Brand(s)</Label><Input data-testid="lot-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} className="mt-1.5 rounded-sm" placeholder="e.g. Bosch, Valeo" /></div>
              <div><Label>City</Label><Input data-testid="lot-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5 rounded-sm" placeholder="City" /></div>
              <div><Label>State</Label>
                <Select value={form.state} onValueChange={(v) => set("state", v)}><SelectTrigger className="mt-1.5 rounded-sm" data-testid="lot-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Parts Condition</Label>
                <Select value={form.condition} onValueChange={(v) => set("condition", v)}><SelectTrigger className="mt-1.5 rounded-sm" data-testid="lot-condition"><SelectValue placeholder="Select parts condition" /></SelectTrigger>
                  <SelectContent>{PARTS_CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Packaging Condition</Label>
                <Select value={form.packaging_condition} onValueChange={(v) => set("packaging_condition", v)}><SelectTrigger className="mt-1.5 rounded-sm" data-testid="lot-packaging-condition"><SelectValue placeholder="Select packaging condition" /></SelectTrigger>
                  <SelectContent>{PACKAGING_CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Commercials" step="2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Total units</Label><Input data-testid="lot-units" type="number" value={form.total_units} onChange={(e) => set("total_units", e.target.value)} className="mt-1.5 rounded-sm font-mono" /></div>
            <div><Label>Unique SKUs</Label><Input data-testid="lot-skus" type="number" value={form.unique_skus} onChange={(e) => set("unique_skus", e.target.value)} className="mt-1.5 rounded-sm font-mono" /></div>
            <div><Label>Total MRP value (₹) *</Label><Input data-testid="lot-mrp" type="number" value={form.mrp_value} onChange={(e) => set("mrp_value", e.target.value)} className="mt-1.5 rounded-sm font-mono" placeholder="Min 500000" /></div>
            <div><Label>Asking price (₹) *</Label><Input data-testid="lot-asking" type="number" value={form.asking_price} onChange={(e) => set("asking_price", e.target.value)} className="mt-1.5 rounded-sm font-mono" /></div>
          </div>
          {Number(form.mrp_value) > 0 && Number(form.mrp_value) < 500000 && <p className="mt-3 text-sm text-destructive">Lot MRP value must be at least ₹5,00,000 to submit.</p>}
        </Section>

        <Section title="Inventory file & media" step="3">
          <div className="mb-5 flex flex-col gap-3 rounded-sm border border-brand/30 bg-brand/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <FileXls size={22} className="mt-0.5 shrink-0 text-brand" />
              <div>
                <div className="text-sm font-semibold text-navy">Use the SparesHub lot format</div>
                <p className="text-xs text-muted-foreground">Download the template, fill your inventory in the prescribed format, then upload the file below.</p>
              </div>
            </div>
            <a href="/lot-format-template.xlsx" download className="inline-flex shrink-0 items-center justify-center gap-2 rounded-sm bg-brand px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-hover" data-testid="download-template-button"><DownloadSimple size={16} /> Download Excel format</a>
          </div>

          <Label>Original inventory Excel (kept private, never shown to buyers)</Label>
          <label className="mt-1.5 flex cursor-pointer items-center gap-3 rounded-sm border border-dashed border-border bg-navy-50 p-4 hover:border-navy/40" data-testid="excel-upload-label">
            <FileXls size={24} className="text-navy" />
            <div className="flex-1 text-sm">
              {excel ? <span className="font-medium text-navy">{excel.filename}</span> : form.original_excel_file_id ? <span className="font-medium text-navy">Inventory file attached</span> : <span className="text-muted-foreground">Upload .xlsx / .csv inventory file</span>}
            </div>
            <UploadSimple className="text-brand" />
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="excel-input" onChange={(e) => e.target.files[0] && uploadExcel(e.target.files[0])} />
          </label>

          <Label className="mt-6 block">Lot photos — 4 to 8 required <span className="stat font-semibold text-navy" data-testid="image-count">({images.length}/8)</span></Label>
          <label className="mt-1.5 flex cursor-pointer items-center gap-3 rounded-sm border border-dashed border-border bg-navy-50 p-4 hover:border-navy/40" data-testid="image-upload-label">
            <ImageIcon size={24} className="text-navy" />
            <div className="flex-1 text-sm text-muted-foreground">Upload 4 to 8 images (JPG / PNG / WebP)</div>
            <UploadSimple className="text-brand" />
            <input type="file" accept="image/*" multiple className="hidden" data-testid="image-input" onChange={(e) => e.target.files.length && uploadImages([...e.target.files])} />
          </label>
          {images.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
              {images.map((im) => (
                <div key={im.id} className="group relative aspect-square overflow-hidden rounded-sm border border-border">
                  <img src={im.url} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => removeImage(im.id)} data-testid={`remove-image-${im.id}`} className="absolute right-1 top-1 rounded-sm bg-navy/80 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"><X size={14} /></button>
                </div>
              ))}
            </div>
          )}

          <Label className="mt-6 block">Lot video — minimum 1 required, up to 600 seconds (10 min) each <span className="stat font-semibold text-navy" data-testid="video-count">({videos.length}/1)</span></Label>
          <label className="mt-1.5 flex cursor-pointer items-center gap-3 rounded-sm border border-dashed border-border bg-navy-50 p-4 hover:border-navy/40" data-testid="video-upload-label">
            <FilmSlate size={24} className="text-navy" />
            <div className="flex-1 text-sm text-muted-foreground">Upload a walkthrough video (MP4 / MOV / WebM, ≤ 600 seconds)</div>
            <UploadSimple className="text-brand" />
            <input type="file" accept="video/*" multiple className="hidden" data-testid="video-input" onChange={(e) => e.target.files.length && uploadVideos([...e.target.files])} />
          </label>
          {videos.length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {videos.map((vi) => (
                <div key={vi.id} className="group relative overflow-hidden rounded-sm border border-border bg-black">
                  <video src={vi.url} className="h-32 w-full object-cover" controls preload="metadata" />
                  <button onClick={() => removeVideo(vi.id)} data-testid={`remove-video-${vi.id}`} className="absolute right-1 top-1 rounded-sm bg-navy/80 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"><X size={14} /></button>
                </div>
              ))}
            </div>
          )}
          {uploading && <p className="mt-2 text-sm text-muted-foreground">Uploading...</p>}
        </Section>

        <Section title="Sample inventory preview" step="4">
          <p className="mb-3 text-sm text-muted-foreground">Add a few representative SKUs shown publicly on the lot page.</p>
          <div className="space-y-2">
            {form.sample_inventory.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <Input placeholder="SKU" value={r.sku} onChange={(e) => setRow(i, "sku", e.target.value)} className="col-span-3 rounded-sm font-mono text-xs" data-testid={`sample-sku-${i}`} />
                <Input placeholder="Description" value={r.description} onChange={(e) => setRow(i, "description", e.target.value)} className="col-span-5 rounded-sm text-xs" data-testid={`sample-desc-${i}`} />
                <Input placeholder="Qty" type="number" value={r.qty} onChange={(e) => setRow(i, "qty", e.target.value)} className="col-span-2 rounded-sm font-mono text-xs" data-testid={`sample-qty-${i}`} />
                <Input placeholder="MRP" type="number" value={r.mrp} onChange={(e) => setRow(i, "mrp", e.target.value)} className="col-span-1 rounded-sm font-mono text-xs" data-testid={`sample-mrp-${i}`} />
                <button onClick={() => delRow(i)} className="col-span-1 flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash size={16} /></button>
              </div>
            ))}
          </div>
          <Button variant="outline" onClick={addRow} className="mt-3 rounded-sm" data-testid="add-sample-row"><Plus className="mr-1" size={14} /> Add row</Button>
        </Section>

        <Section title="Declarations" step="5">
          <div className="space-y-3">
            {DECLARATIONS.map((d, i) => (
              <label key={i} className="flex cursor-pointer items-start gap-3 text-sm text-navy" data-testid={`declaration-${i}`}>
                <Checkbox checked={decls[i]} onCheckedChange={(v) => setDecls((arr) => { const n = [...arr]; n[i] = Boolean(v); return n; })} className="mt-0.5" />
                {d}
              </label>
            ))}
          </div>
        </Section>

        <div className="flex flex-wrap gap-3">
          <Button onClick={() => save(true)} disabled={saving} className="h-12 rounded-sm bg-brand px-8 text-base font-semibold text-white hover:bg-brand-hover" data-testid="submit-lot-button">
            <CheckCircle className="mr-1" /> {saving ? (isAdmin ? "Publishing..." : "Submitting...") : isAdmin ? "Publish lot" : "Submit for verification"}
          </Button>
          <Button onClick={() => save(false)} disabled={saving} variant="outline" className="h-12 rounded-sm px-8 text-base" data-testid="save-draft-button">{isAdmin ? "Save changes" : "Save as draft"}</Button>
        </div>
      </div>
    </div>
  );
}
